import type { BankSlipItem, Invoice, OrganizationType } from './types';

export type InvoiceFilters = {
  beginDate: string | null;
  endDate: string | null;
  invoiceNumber: string | null;
  beginAmount: string | null;
  endAmount: string | null;
};

export const PAGE_SIZE = 5;

export const ORDER_BY: Record<OrganizationType, string> = {
  maiorValor: 'amountOriginal DESC',
  menorValor: 'amountOriginal ASC',
  notaMaior: 'invoiceNumber DESC',
  notaMenor: 'invoiceNumber ASC',
  maiorData: 'emissionDate DESC',
  menorData: 'emissionDate ASC',
};

export function bankSlipsFromResponse(response: Invoice[]): BankSlipItem[] {
  return response.flatMap((invoice) =>
    invoice.installments.map((installment) => ({ invoice, installment }))
  );
}

export function isInvoiceDownloadable(invoice: Invoice): boolean {
  return (
    (invoice.source === 'COLETORES' || invoice.source === 'NFF') &&
    invoice.accessKey !== undefined
  );
}

const createBaseParams = (
  customerCode: string | null,
  customerSiteCode: string | null,
  activePage: number,
  orderBy: string
): URLSearchParams => {
  const params = new URLSearchParams();

  if (customerSiteCode) {
    params.set('customer-site-code', customerSiteCode);
  }

  if (customerCode) {
    params.set('customer-code', customerCode);
  }

  params.set('_expand', 'installments');
  params.set('_limit', String(PAGE_SIZE));
  params.set('_offset', String(activePage - 1));
  if (orderBy) {
    params.set('_order_by', orderBy);
  }

  return params;
};

export const FILTER_FETCH_LIMIT = 100000;

export type BuildInvoicesUrlOptions = {
  bulkFetch?: boolean;
};

export function buildInvoicesUrl(
  customerCode: string | null,
  customerSiteCode: string | null,
  activePage: number,
  orderBy: string,
  filters: InvoiceFilters,
  options: BuildInvoicesUrlOptions = {}
): string {
  const { beginDate, endDate, invoiceNumber, beginAmount, endAmount } = filters;
  const params = createBaseParams(
    customerCode,
    customerSiteCode,
    activePage,
    orderBy
  );

  if (options.bulkFetch) {
    params.set('_limit', String(FILTER_FETCH_LIMIT));
    params.set('_offset', '0');
  }

  const hasDate = Boolean(beginDate && endDate);
  const hasAmount = Boolean(beginAmount && endAmount);
  const hasInvoice = Boolean(invoiceNumber);

  if (hasDate && !hasInvoice && !hasAmount) {
    params.set('begin-date', beginDate!);
    params.set('end-date', endDate!);
  } else if (hasInvoice && hasDate && !hasAmount) {
    params.set('begin-date', beginDate!);
    params.set('end-date', endDate!);
    params.set('invoice-number', invoiceNumber!);
  } else if (hasAmount && hasDate && !hasInvoice) {
    params.set('begin-date', beginDate!);
    params.set('end-date', endDate!);
    params.set('begin-amount', beginAmount!);
    params.set('end-amount', endAmount!);
  } else if (hasAmount && hasInvoice && hasDate) {
    params.set('begin-date', beginDate!);
    params.set('end-date', endDate!);
    params.set('begin-amount', beginAmount!);
    params.set('end-amount', endAmount!);
    params.set('invoice-number', invoiceNumber!);
  } else if (hasAmount && hasInvoice && !hasDate) {
    params.set('begin-amount', beginAmount!);
    params.set('end-amount', endAmount!);
    params.set('invoice-number', invoiceNumber!);
  } else if (hasAmount && !hasDate && !hasInvoice) {
    params.set('begin-amount', beginAmount!);
    params.set('end-amount', endAmount!);
  } else if (hasInvoice && !hasDate && !hasAmount) {
    params.set('invoice-number', invoiceNumber!);
  }

  return `industrial/invoice/v1/invoices?${params.toString()}`;
}

export function getInstallmentStatus(installment: {
  dueDate: string;
  paymentStatus: string;
}): string {
  const currentDate = new Date();
  const dueDate = new Date(installment.dueDate);
  dueDate.setDate(dueDate.getDate() + 1);

  if (installment.paymentStatus !== 'OP') {
    return 'Pago';
  }

  return currentDate > dueDate ? 'Vencido' : 'A vencer';
}

export function getEmptyMessage(loading: boolean, hasFilter: boolean): string {
  if (loading) return 'Carregando...';
  if (hasFilter) return 'Você não possui notas para esse filtro!';
  return 'Você não possui notas!';
}
